import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  createVehicle,
  fetchAlertConfig,
  fetchFleetOverview,
  fetchVehicles,
  maintenanceLabel,
  updateAlertConfig,
  updateVehicle,
  type AlertConfig,
  type FleetOverview,
  type VehicleInput,
  type VehicleStatus,
  type VehicleSummary,
} from "@/lib/adminFleetApi";
import { formatBrDate, formatKm } from "@/lib/fleetFormat";
import { FleetAlertBadge } from "@/components/admin/FleetAlertBadge";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Car, ChevronRight, Loader2, Pencil, Plus, Search, Settings2 } from "lucide-react";

interface VehicleDraft {
  plate: string;
  brand: string;
  model: string;
  year: string;
  currentMileage: string;
  status: VehicleStatus;
}

const EMPTY_DRAFT: VehicleDraft = { plate: "", brand: "", model: "", year: "", currentMileage: "", status: "ACTIVE" };

export default function AdminVehicles() {
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const canCreateVeh = hasPermission(account, "FROTA.VEICULO.CRIAR");
  const canEditVeh = hasPermission(account, "FROTA.VEICULO.EDITAR");
  const canEditAlert = hasPermission(account, "FROTA.ALERTA.EDITAR");
  const [vehicles, setVehicles] = useState<VehicleSummary[]>([]);
  const [overview, setOverview] = useState<FleetOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<VehicleDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState<AlertConfig>({ alertDaysAhead: 30, alertKmAhead: 1000 });
  const [savingConfig, setSavingConfig] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, ov] = await Promise.all([fetchVehicles(), fetchFleetOverview()]);
      setVehicles(list);
      setOverview(ov);
    } catch {
      toast.error("Erro ao carregar veículos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setEditorOpen(true);
  };

  const openEdit = (v: VehicleSummary) => {
    setEditingId(v.id);
    setDraft({
      plate: v.plate,
      brand: v.brand ?? "",
      model: v.model ?? "",
      year: v.year != null ? String(v.year) : "",
      currentMileage: String(v.currentMileage),
      status: v.status,
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!draft.plate.trim()) { toast.error("Placa é obrigatória"); return; }
    setSaving(true);
    try {
      const payload: VehicleInput = {
        plate: draft.plate.trim(),
        brand: draft.brand.trim() || undefined,
        model: draft.model.trim() || undefined,
        year: draft.year.trim() ? Number(draft.year) : null,
        status: draft.status,
      };
      if (editingId) {
        await updateVehicle(editingId, payload);
        toast.success("Veículo atualizado");
      } else {
        payload.currentMileage = draft.currentMileage.trim() ? Number(draft.currentMileage) : 0;
        await createVehicle(payload);
        toast.success("Veículo cadastrado");
      }
      setEditorOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar veículo");
    } finally {
      setSaving(false);
    }
  };

  const openConfig = async () => {
    try {
      setConfig(await fetchAlertConfig());
    } catch {
      /* mantém defaults se falhar */
    }
    setConfigOpen(true);
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const saved = await updateAlertConfig({
        alertDaysAhead: Math.max(0, Number(config.alertDaysAhead) || 0),
        alertKmAhead: Math.max(0, Number(config.alertKmAhead) || 0),
      });
      setConfig(saved);
      toast.success("Antecedência de alerta atualizada");
      setConfigOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar configuração");
    } finally {
      setSavingConfig(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) =>
      v.plate.toLowerCase().includes(q) ||
      (v.brand ?? "").toLowerCase().includes(q) ||
      (v.model ?? "").toLowerCase().includes(q));
  }, [vehicles, search]);

  return (
    <div className="container flex flex-col gap-6 py-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Manutenção de veículos</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Controle das manutenções preventivas da frota.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEditAlert && (
              <Button variant="secondary" onClick={() => void openConfig()} className="gap-2">
                <Settings2 className="h-4 w-4" /> Configurar alertas
              </Button>
            )}
            {canCreateVeh && (
              <Button variant="secondary" onClick={openCreate} className="gap-2">
                <Plus className="h-4 w-4" /> Novo veículo
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
          <Search className="h-4 w-4 text-primary-foreground/60" />
          <input
            className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
            placeholder="Buscar por placa, marca ou modelo"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Resumo de alertas da frota */}
      {overview && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard label="Veículos ativos" value={overview.activeVehicles} tone="neutral" />
          <SummaryCard label="🟢 Em dia" value={overview.emDia} tone="green" />
          <SummaryCard label="🟡 Próximas" value={overview.proxima} tone="amber" />
          <SummaryCard label="🔴 Vencidas" value={overview.vencida} tone="red" />
        </div>
      )}

      {overview && overview.alerts.length > 0 && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="border-b px-4 py-3 text-sm font-semibold">Manutenções em alerta</div>
          <ul className="divide-y">
            {overview.alerts.map((a) => (
              <li key={`${a.vehicleId}-${a.maintenanceId}`}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/frota/${a.vehicleId}`)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50"
                >
                  <FleetAlertBadge status={a.alertStatus} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {a.plate}
                      {a.brand ? ` · ${a.brand}` : ""}{a.model ? ` ${a.model}` : ""}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {maintenanceLabel(a.type, a.customName)} · prev.: {formatBrDate(a.nextServiceDate)}
                      {a.nextServiceMileage != null ? ` / ${formatKm(a.nextServiceMileage)}` : ""}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tabela de veículos */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Car className="h-10 w-10 opacity-30" />
          <p className="text-sm">{search ? "Nenhum veículo encontrado" : "Nenhum veículo cadastrado"}</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Situação</TableHead>
                <TableHead>Placa</TableHead>
                <TableHead>Marca / Modelo</TableHead>
                <TableHead>Ano</TableHead>
                <TableHead>Km atual</TableHead>
                <TableHead>Alertas</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow
                  key={v.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/admin/frota/${v.id}`)}
                >
                  <TableCell><FleetAlertBadge status={v.alertStatus} /></TableCell>
                  <TableCell className="font-medium">{v.plate}</TableCell>
                  <TableCell className="text-sm">
                    {v.brand ?? "—"}{v.model ? ` ${v.model}` : ""}
                    {v.status === "INACTIVE" && (
                      <Badge variant="secondary" className="ml-2">Inativo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{v.year ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatKm(v.currentMileage)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {v.dueCount > 0 && <span className="text-red-600">{v.dueCount} vencida(s) </span>}
                    {v.upcomingCount > 0 && <span className="text-amber-600">{v.upcomingCount} próxima(s)</span>}
                    {v.dueCount === 0 && v.upcomingCount === 0 && "—"}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {canEditVeh && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Editor de veículo */}
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) setEditorOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar veículo" : "Novo veículo"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="v-plate">Placa *</Label>
              <Input
                id="v-plate"
                value={draft.plate}
                onChange={(e) => setDraft((d) => ({ ...d, plate: e.target.value.toUpperCase() }))}
                placeholder="ABC1D23"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="v-brand">Marca</Label>
                <Input id="v-brand" value={draft.brand} onChange={(e) => setDraft((d) => ({ ...d, brand: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="v-model">Modelo</Label>
                <Input id="v-model" value={draft.model} onChange={(e) => setDraft((d) => ({ ...d, model: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="v-year">Ano</Label>
                <Input id="v-year" inputMode="numeric" value={draft.year} onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value.replace(/\D/g, "") }))} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="v-km">Km atual {editingId && <span className="text-xs text-muted-foreground">(via registro de km)</span>}</Label>
                <Input
                  id="v-km"
                  inputMode="numeric"
                  value={draft.currentMileage}
                  disabled={!!editingId}
                  onChange={(e) => setDraft((d) => ({ ...d, currentMileage: e.target.value.replace(/\D/g, "") }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Situação</Label>
              <Select value={draft.status} onValueChange={(val) => setDraft((d) => ({ ...d, status: val as VehicleStatus }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Ativo</SelectItem>
                  <SelectItem value="INACTIVE">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingId ? "Salvar" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config de alertas */}
      <Dialog open={configOpen} onOpenChange={(open) => { if (!open) setConfigOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Antecedência dos alertas</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Uma manutenção fica <strong>🟡 Próxima</strong> ao entrar nesta antecedência (por data ou km).
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="c-days">Alertar quantos dias antes</Label>
              <Input
                id="c-days"
                inputMode="numeric"
                value={String(config.alertDaysAhead)}
                onChange={(e) => setConfig((c) => ({ ...c, alertDaysAhead: Number(e.target.value.replace(/\D/g, "")) || 0 }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="c-km">Alertar quantos km antes</Label>
              <Input
                id="c-km"
                inputMode="numeric"
                value={String(config.alertKmAhead)}
                onChange={(e) => setConfig((c) => ({ ...c, alertKmAhead: Number(e.target.value.replace(/\D/g, "")) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfigOpen(false)} disabled={savingConfig}>Cancelar</Button>
            <Button onClick={() => void handleSaveConfig()} disabled={savingConfig}>
              {savingConfig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: "neutral" | "green" | "amber" | "red" }) {
  const toneClass =
    tone === "green" ? "text-emerald-600"
      : tone === "amber" ? "text-amber-600"
        : tone === "red" ? "text-red-600"
          : "text-foreground";
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}
